// 前后端共用的类型。字段命名与 design_handoff/docs/data-model.md 保持一致。

export const ROLES = ['member', 'manager', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const MEMBER_STATUSES = ['invited', 'active', 'offboarding', 'left'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

export const PROJECT_STATUSES = ['todo', 'progress', 'blocked', 'done', 'cancelled'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** 存储态。“逾期”由 draft + 已过截止时间推导，不单独存。 */
export const REPORT_STATUSES = ['draft', 'submitted', 'on_leave'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export type PrefillSource = 'plan' | 'none' | 'manual';
export type ItemOrigin = 'ai' | 'edited' | 'manual';
export type FollowUpKind = 'ai' | 'manager';
export type Channel = 'web' | 'wechat';

export const ANOMALY_TYPES = ['blocked_timeout', 'due_slipped', 'stalled', 'late_start', 'said_not_done'] as const;
export type AnomalyType = (typeof ANOMALY_TYPES)[number];
export const ANOMALY_TYPE_LABEL: Record<AnomalyType, string> = {
  blocked_timeout: '阻塞超时',
  due_slipped: '日期反复延期',
  stalled: '项目停滞',
  late_start: '晚启动',
  said_not_done: '说了没做',
};

export type AnomalyStatus = 'open' | 'asked' | 'replied' | 'assigned' | 'closed';
export type ResolutionKind = 'resolved' | 'following' | 'false_positive';
export const RESOLUTION_LABEL: Record<ResolutionKind, string> = {
  resolved: '已解决',
  following: '有人跟进，等待结果',
  false_positive: '不需要处理',
};

export type AssignmentStatus = 'open' | 'done' | 'returned' | 'reassigned';
export type ExitRequestStatus = 'pending' | 'approved' | 'rejected';
export type LeaveType = 'leave' | 'trip' | 'rest';
export const LEAVE_LABEL: Record<LeaveType, string> = { leave: '请假', trip: '出差', rest: '休息' };
export type AIFeedbackReason = 'wrong_project' | 'wrong_progress' | 'others_work' | 'too_vague' | 'other';

// ---------- 人员 ----------
export interface TeamDTO {
  id: string;
  name: string;
  initial: string;
}

export interface MemberDTO {
  id: string;
  name: string;
  initial: string;
  role: Role;
  status: MemberStatus;
  teamId: string | null;
  teamName: string | null;
  managerId: string | null;
  /** 侧栏底部右侧的说明：团队名或“管理层” */
  title: string | null;
  /** 指派面板里的一句职责说明，如“负责本地化材料” */
  responsibility: string | null;
  projectCount: number;
}

export interface MeDTO extends MemberDTO {
  phoneMasked: string | null;
  email: string | null;
  /** 个人微信是否已绑定（扫码登录用） */
  wechatBound: boolean;
  onboarded: boolean;
  isManager: boolean;
  workspace: { name: string; timezone: string };
  /** 服务器当前时间（支持开发时的时钟偏移），前端据此校准 */
  serverNow: string;
  inboxCount: number;
  /** 站内通知里未读的条数 */
  unreadNotifications: number;
  teams: TeamDTO[];
  /** 能看到的项目集数量（侧栏是否显示「项目集」） */
  programCount: number;
}

// ---------- 本地智能体（MCP） ----------
export interface McpTokenDTO {
  exists: boolean;
  /** 令牌末 4 位 */
  hint: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/** 用连接器（OAuth）接入的 Claude 应用，例如手机和网页版 Claude */
export interface OAuthGrantDTO {
  id: string;
  clientName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

// ---------- 问 AI ----------
export interface AskSourceDTO {
  kind: 'progress' | 'note' | 'plan' | 'comment';
  date: string;
  memberId: string;
  memberName: string;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  text: string;
}

export interface AskAnswerDTO {
  question: string;
  answer: string;
  /** 回答引用的日报原文 */
  sources: AskSourceDTO[];
  /** 这次查了哪些日报，如“9月4日 以来的日报” */
  scope: string;
  /** 用的是哪个常见问题 */
  preset: string | null;
}

// ---------- 管理层看板 ----------
export interface SubmitStats {
  /** 应交的份数（工作日 × 在岗、有项目、没请假的人） */
  expected: number;
  onTime: number;
  late: number;
  missing: number;
}

export interface DashboardDTO {
  /** “全公司”或“销售部”等 */
  scope: string;
  people: number;
  today: {
    date: string;
    workday: boolean;
    expected: number;
    submitted: number;
    /** 已过截止时间 */
    afterDeadline: boolean;
    deadline: string;
    pending: { id: string; name: string }[];
  };
  week: { weekStart: string; total: SubmitStats; teams: (SubmitStats & { teamId: string | null; name: string; people: number })[] };
  /** 近 6 周，从早到晚 */
  trend: (SubmitStats & { weekStart: string; anomalies: number })[];
  projects: {
    todo: number;
    progress: number;
    blocked: number;
    doneThisMonth: number;
    flagged: { id: string; code: string; name: string; ownerName: string; status: ProjectStatus; signals: string[] }[];
    due: { id: string; code: string; name: string; ownerName: string; dueDate: string; overdue: boolean }[];
  };
  issues: {
    openAnomalies: number;
    newThisWeek: number;
    closedThisWeek: number;
    oldestOpenDays: number | null;
    openAssignments: number;
    overdueAssignments: number;
  };
}

// ---------- 节假日导入 ----------
export interface OfficialHolidaysDTO {
  year: number;
  holidays: string[];
  /** 调休上班的周末 */
  extraWorkdays: string[];
  /** 包含的假期，如 ["元旦","春节",…] */
  names: string[];
  /** 国务院办公厅放假通知原文 */
  source: string | null;
}

// ---------- 通知 ----------
export interface NotificationDTO {
  id: string;
  kind: string;
  /** 小标签，如“日报提醒”“追问” */
  label: string;
  title: string;
  body: string;
  /** 站内路径，如 /projects/xxx */
  link: string | null;
  createdAt: string;
  read: boolean;
}

// ---------- 项目 ----------
export interface ProjectMetric {
  label: string | null;
  value: number | null;
  target: number | null;
  unit: string | null;
}

export type HealthKind = 'blocked' | 'stalled' | 'due_slipped' | 'late_start' | 'owner_on_leave';
export interface HealthSignal {
  kind: HealthKind;
  /** 如“阻塞 3 天”“停滞 6 天”“延期 3 次”“晚启动 5 天”“负责人休假中” */
  label: string;
}

export interface ProjectDTO {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  ownerId: string;
  ownerName: string;
  participantIds: string[];
  teamId: string | null;
  customer: string | null;
  phase: string | null;
  phaseIndex: number | null;
  phaseCount: number | null;
  startDate: string | null;
  dueDate: string | null;
  dueDateChanges: number;
  nextStep: { text: string; date: string | null; assigneeId: string | null } | null;
  metric: ProjectMetric | null;
  blockedSince: string | null;
  blockedReason: string | null;
  health: HealthSignal[];
  lastProgressDate: string | null;
}

/** 雷达列表的一行（设计稿 7a / 14b） */
export interface RadarProjectDTO extends ProjectDTO {
  /** 能看项目内容；为 false 时只有名录信息（编号、名称、负责人、团队、状态），进展等字段已清空 */
  visible: boolean;
  mine: boolean;
  teamName: string | null;
  ownerInitial: string;
  /** 最近一条有内容的日报（不论谁写的） */
  latest: { text: string; date: string; memberName: string } | null;
  doneAt: string | null;
}

export interface PersonRef {
  id: string;
  name: string;
  initial: string;
}

export interface DueChangeDTO {
  from: string | null;
  to: string;
  byName: string;
  at: string;
  reason: string | null;
}

export type ProjectTimelineEntry =
  | {
      kind: 'report';
      at: string;
      date: string;
      member: PersonRef;
      text: string;
      noProgress: boolean;
      /** 撤回修改后，这里显示的是上一个提交版本 */
      revised: boolean;
      /** 撤回后改过（设计稿 19a）：最早提交的原文和时间点 */
      revision?: { original: string; submittedAt: string | null; retractedAt: string; resubmittedAt: string | null } | null;
      attachments: AttachmentDTO[];
    }
  | { kind: 'due_change'; at: string; byName: string; from: string | null; to: string; reason: string | null }
  | { kind: 'status'; at: string; byName: string | null; from: ProjectStatus; to: ProjectStatus }
  | { kind: 'follow_up'; at: string; followUp: FollowUpDTO; aboutName: string }
  | { kind: 'comment'; at: string; id: string; author: PersonRef; text: string; attachments: AttachmentDTO[] };

/** 项目详情（设计稿 8a） */
export interface ProjectDetailDTO {
  project: RadarProjectDTO & { description: string | null };
  owner: PersonRef;
  participants: PersonRef[];
  nextStepAssignee: PersonRef | null;
  dueChanges: DueChangeDTO[];
  dependents: { id: string; code: string; name: string; status: ProjectStatus }[];
  dependsOn: { id: string; code: string; name: string; status: ProjectStatus }[];
  timeline: ProjectTimelineEntry[];
  canEdit: boolean;
  /** 能看项目内容；为 false 时时间线只有和自己有关的讨论 */
  canView: boolean;
  /** 能发评论：能看项目内容，或者在评论里被 @ 过 */
  canComment: boolean;
}

/** 收件箱「提到我」 */
export interface MentionDTO {
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  author: PersonRef;
  text: string;
  createdAt: string;
}

// ---------- 日报 ----------
/** 附件（下载地址是 /api/attachments/:id） */
export interface AttachmentDTO {
  id: string;
  name: string;
  mime: string;
  size: number;
  uploaderId: string;
}

export const ATTACHMENT_MAX_BYTES = 200 * 1024 * 1024;
export const ATTACHMENTS_PER_TARGET = 5;

export interface ReportItemDTO {
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  projectStatus: ProjectStatus;
  text: string;
  /** 预填时的原文，用于判断“已修改” */
  prefilledText: string | null;
  origin: ItemOrigin;
  noProgress: boolean;
  aiSkipped: boolean;
  /** AI 追问指向的那句话（VaguePhrase 虚线下划线） */
  vaguePhrase: string | null;
  followUpId: string | null;
  position: number;
  attachments: AttachmentDTO[];
}

export interface PlanItemDTO {
  id: string;
  projectId: string | null;
  projectCode: string | null;
  text: string;
  checked: boolean;
  suggestedByAI: boolean;
  position: number;
}

export interface FollowUpDTO {
  id: string;
  kind: FollowUpKind;
  fromId: string | null;
  fromName: string;
  fromInitial: string;
  toId: string;
  toName: string;
  ccIds: string[];
  ccNames: string[];
  reportItemId: string | null;
  anomalyId: string | null;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  /** 追问针对的日报日期（“关于你 10月3日 的日报”） */
  aboutDate: string | null;
  question: string;
  vaguePhrase: string | null;
  suggestedAnswers: string[];
  answer: string | null;
  answeredAt: string | null;
  answerChannel: Channel | null;
  skipped: boolean;
  askedAt: string;
}

export interface YesterdayPlanDTO {
  text: string;
  projectCode: string | null;
  /** done：今日进展里写到；not_done：没写到；explained：没做完但写了原因 */
  state: 'done' | 'not_done' | 'explained';
}

export interface ReportSummaryDTO {
  date: string;
  status: ReportStatus;
  submittedAt: string | null;
  late: boolean;
}

export interface LeaveDTO {
  id: string;
  type: LeaveType;
  from: string;
  to: string;
  note: string | null;
  notifyOwners: boolean;
}

export interface ReportPageDTO {
  id: string;
  memberId: string;
  date: string;
  status: ReportStatus;
  /** draft 且已过截止时间 */
  overdue: boolean;
  late: boolean;
  prefillSource: PrefillSource;
  prefillFromDate: string | null;
  submittedAt: string | null;
  updatedAt: string;
  note: string | null;
  items: ReportItemDTO[];
  plans: PlanItemDTO[];
  /** 本份日报上的 AI 追问 */
  aiFollowUps: FollowUpDTO[];
  /** 发给我、还没回复的主管追问（置顶） */
  managerFollowUps: FollowUpDTO[];
  yesterdayPlans: YesterdayPlanDTO[];
  /** 顶部读数格（最多 3 个项目） */
  readouts: ProjectDTO[];
  recent: ReportSummaryDTO[];
  deadline: string;
  submitTo: { teamName: string | null; managerName: string | null };
  leave: LeaveDTO | null;
  canRetract: boolean;
  /** 首次使用：还没确认负责的项目 */
  needsOnboarding: boolean;
  limits: { itemMaxChars: number; planMax: number };
}

// ---------- 收件箱 ----------
export interface EvidenceDTO {
  reportId: string | null;
  memberId: string;
  memberName: string;
  date: string;
  /** report：日报原文；ai_reply：回复 AI 追问；manager_reply：回复主管追问 */
  source: 'report' | 'ai_reply' | 'manager_reply';
  quote: string;
  /** 撤回修改后原文已变 */
  revised: boolean;
}

export interface TimelineEventDTO {
  at: string;
  text: string;
}

export interface ResolutionDTO {
  kind: ResolutionKind;
  note: string | null;
  recheckAt: string | null;
  byId: string;
  at: string;
}

export interface AssignmentDTO {
  id: string;
  anomalyId: string;
  assigneeId: string;
  assigneeName: string;
  assignerId: string;
  assignerName: string;
  assignerInitial: string;
  ask: string;
  dueAt: string;
  status: AssignmentStatus;
  reply: string | null;
  returnReason: string | null;
  createdAt: string;
  completedAt: string | null;
  anomalyTitle: string;
  projectCode: string;
  projectName: string;
  /** 被指派人看到的背景（异常的 AI 判断） */
  background: string | null;
  evidenceCount: number;
}

export interface AnomalyDTO {
  id: string;
  type: AnomalyType;
  typeLabel: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  ownerId: string;
  ownerName: string;
  involvedNames: string[];
  managerId: string;
  title: string;
  judgement: string;
  evidence: EvidenceDTO[];
  timeline: TimelineEventDTO[];
  suggestions: string[];
  status: AnomalyStatus;
  resolution: ResolutionDTO | null;
  followUps: FollowUpDTO[];
  assignment: AssignmentDTO | null;
  generatedAt: string;
  date: string;
  rank: number;
}

export interface ExitRequestDTO {
  id: string;
  memberId: string;
  memberName: string;
  memberInitial: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  reason: string;
  handoverToId: string | null;
  handoverToName: string | null;
  status: ExitRequestStatus;
  createdAt: string;
  pendingPlans: PlanItemDTO[];
  recentReportCount: number;
}

export interface JoinRequestDTO {
  id: string;
  email: string;
  createdAt: string;
}

export interface InboxDTO {
  date: string;
  generatedAt: string | null;
  anomalies: AnomalyDTO[];
  assignedToMe: AssignmentDTO[];
  exitRequests: ExitRequestDTO[];
  /** 发给我的主管追问 / 需要回答的 AI 追问 */
  followUpsToMe: FollowUpDTO[];
  /** 管理员：申请加入工作区的人 */
  joinRequests: JoinRequestDTO[];
  /** 最近 7 天项目评论里 @ 到我的 */
  mentions: MentionDTO[];
  /** 离职交接：等我确认接手的项目（设计稿 22d） */
  handovers: HandoverDTO[];
  /** 主管发来的消息：约 1:1、感谢（设计稿 19c） */
  messages: MemberMessageDTO[];
  counts: { pending: number; handled: number };
  nextCheck: string;
}

// ---------- 设置 ----------
export interface Settings {
  deadline: string;
  prefillReminder: string;
  lateReminder: string;
  rulesAt: string;
  /** 法定节假日（YYYY-MM-DD），这些日子不算工作日 */
  holidays: string[];
  /** 调休上班的周末 */
  extraWorkdays: string[];
  blockedTimeoutDays: number;
  dueSlipCount: number;
  stalledDays: number;
  saidNotDoneStreak: number;
  inboxDailyMax: number;
  aiFollowUpsPerReport: number;
  itemMaxChars: number;
  planMax: number;
  retractUntil: string;
  followUpRequeueHours: number;
  /** 0 表示永久保留 */
  reportRetentionYears: number;
  insightRetentionWeeks: number;
  /** false 时只按周一至周五算工作日，不看节假日表 */
  followHolidays: boolean;
  /** 周报自动起草：周几（1–7）几点 */
  weeklyDraftEnabled: boolean;
  weeklyDraftWeekday: number;
  weeklyDraftAt: string;
  notifyPrefill: boolean;
  notifyLate: boolean;
  notifyWeekly: boolean;
  /** “建议关注”名单谁能看：直属主管 / 直属主管和上级 */
  insightVisibility: 'direct' | 'chain';
  /** 项目内容（进展、讨论、健康信号）谁能看：所有人 / 项目相关的人和他们的主管 */
  projectVisibility: 'all' | 'related';
}

/** 改了要等到明天才生效的设置（设计稿 14c：修改后从明天起生效） */
export const DEFERRED_SETTING_KEYS = [
  'deadline',
  'prefillReminder',
  'lateReminder',
  'followHolidays',
  'blockedTimeoutDays',
  'dueSlipCount',
  'stalledDays',
  'saidNotDoneStreak',
  'inboxDailyMax',
] as const satisfies readonly (keyof Settings)[];

export interface SettingsDTO {
  current: Settings;
  /** 已保存、明天起生效的修改 */
  pending: Partial<Settings>;
  pendingFrom: string | null;
  workspace: { name: string; timezone: string; createdAt: string | null };
  meta: {
    aiEnabled: boolean;
    /** deepseek：国内直连；anthropic：Claude（经海外节点）；没配置时为 null */
    aiProvider: 'deepseek' | 'anthropic' | null;
    aiModel: string;
    /** 国内服务器经海外节点调用时，节点地址的主机名 */
    aiRelay: string | null;
    aiCallsToday: { ok: number; failed: number };
    wechatConfigured: boolean;
    smtpConfigured: boolean;
    admins: PersonRef[];
  };
  canEdit: boolean;
}

// ---------- 周报（设计稿 14a） ----------
export type WeeklySection = 'done' | 'progress' | 'blocked' | 'next';

export interface WeeklySource {
  kind: 'report' | 'follow_up' | 'comment' | 'anomaly' | 'project';
  memberName: string | null;
  date: string;
  /** “日报”“回复 AI 追问”“回复周总”“评论”“异常处理”… */
  label: string;
  text: string;
  projectId: string | null;
}

export interface WeeklyItem {
  id: string;
  section: WeeklySection;
  projectId: string | null;
  text: string;
  sources: WeeklySource[];
  edited: boolean;
}

export interface WeeklyDTO {
  /** 还没起草时为 null */
  id: string | null;
  weekStart: string;
  weekEnd: string;
  weekNo: number;
  scope: string;
  scopeName: string;
  scopes: { id: string; name: string }[];
  status: 'draft' | 'sent';
  items: WeeklyItem[];
  reportCount: number;
  anomalyCount: number;
  generatedBy: 'ai' | 'rules' | null;
  generatedAt: string | null;
  editedAt: string | null;
  sentAt: string | null;
  sentByName: string | null;
  recipients: PersonRef[];
  /** 本周还没到自动起草时间 */
  draftAt: string | null;
  canEdit: boolean;
}

// ---------- 团队与成员（设计稿 22c / 22d） ----------
export interface AdminMemberDTO {
  id: string;
  name: string;
  initial: string;
  email: string | null;
  teamId: string | null;
  teamName: string | null;
  role: Role;
  managerId: string | null;
  managerName: string | null;
  title: string | null;
  responsibility: string | null;
  status: MemberStatus;
  /** 有下属 */
  leads: boolean;
  /** 负责（owner）的进行中项目数 */
  ownedCount: number;
  projectCount: number;
  /** 以下三项只给管理员；主管只读查看目录时为 null */
  wechatBound: boolean | null;
  lastLoginAt: string | null;
  invitedAt: string | null;
  /** 开了本地智能体（MCP）令牌；只给管理员 */
  mcpToken: boolean | null;
  offboard: { lastDay: string; handovers: HandoverDTO[] } | null;
}

export interface AdminTeamDTO {
  id: string;
  name: string;
  initial: string;
  memberCount: number;
  projectCount: number;
}

export interface HandoverDTO {
  id: string;
  memberId: string;
  memberName: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  toId: string;
  toName: string;
  lastDay: string;
  status: 'pending' | 'accepted' | 'declined';
  declineReason: string | null;
  createdAt: string;
  decidedAt: string | null;
}

/** 离职交接前的盘点：负责的项目、进行中的事项 */
export interface OffboardPreviewDTO {
  member: PersonRef & { teamName: string | null };
  projects: { id: string; code: string; name: string; status: ProjectStatus; suggestedToId: string | null }[];
  openAnomalies: number;
  openAssignments: number;
  planItems: number;
}

// ---------- 数据与日志（设计稿 22e） ----------
export interface AuditEntryDTO {
  id: string;
  at: string;
  actorName: string | null;
  action: string;
  text: string;
}

// ---------- 团队状态 · 建议关注（设计稿 9a、19b、19c） ----------
export type InsightKind = 'recognize' | 'talk';
export type InsightStatus = 'open' | 'talked' | 'recognized' | 'dismissed';

export interface InsightEvidence {
  memberName: string;
  date: string;
  /** “日报”“回复 AI 追问”“评论”… */
  label: string;
  text: string;
}

export interface TeamInsightDTO {
  id: string;
  weekStart: string;
  member: PersonRef & { teamName: string | null; projects: { id: string; code: string }[] };
  kind: InsightKind;
  headline: string;
  observation: string;
  evidence: InsightEvidence[];
  positives: { date: string; text: string }[];
  impact: string | null;
  suggestions: string[];
  related: { id: string; code: string; name: string }[];
  status: InsightStatus;
  actedAt: string | null;
  meetingAt: string | null;
  /** 最近一次沟通记录（只有写的人能看到备注） */
  talk: { date: string; method: '1:1' | 'phone' | 'wechat'; note: string | null; nextWeek: 'keep_watching' | 'mute_4_weeks' } | null;
  thanks: { text: string; at: string } | null;
  feedbackGiven: boolean;
  generatedBy: 'ai' | 'rules';
  /** 设置为“直属主管和上级”时，上级看到的条目注明直属主管 */
  viaManager: string | null;
}

export interface TeamStatusDTO {
  weekStart: string;
  weekEnd: string;
  reportCount: number;
  generatedAt: string | null;
  /** 本周还没到生成时间 */
  draftAt: string | null;
  directCount: number;
  insights: TeamInsightDTO[];
}

export interface MemberMessageDTO {
  id: string;
  kind: 'one_on_one' | 'thanks';
  from: PersonRef;
  text: string;
  meetingAt: string | null;
  createdAt: string;
  read: boolean;
}

// ---------- 成员页 ----------
export type PersonDayState = 'submitted' | 'late' | 'revising' | 'on_leave' | 'missing' | 'pending';

export interface PersonDayDTO {
  date: string;
  /** submitted 按时提交；late 截止后提交；revising 撤回修改中（显示上一次提交的版本）；pending 今天还没提交 */
  state: PersonDayState;
  submittedAt: string | null;
  leaveType: LeaveType | null;
  items: { projectId: string; code: string; text: string; noProgress: boolean }[];
  note: string | null;
  plans: { text: string; code: string | null }[];
}

export interface PersonDTO {
  member: MemberDTO & { email: string | null; joinedAt: string; wechatBound: boolean | null; offboardDate: string | null };
  manager: PersonRef | null;
  reports: (PersonRef & { teamName: string | null })[];
  /** 今天或接下来 7 天内的请假 / 出差 / 休息 */
  leave: { type: LeaveType; from: string; to: string } | null;
  owned: RadarProjectDTO[];
  participating: RadarProjectDTO[];
  /** 最近 10 个工作日；没有权限看完整日报时为 null */
  days: PersonDayDTO[] | null;
  /** 有参与中的项目、需要写日报（没有项目的人不显示“没有提交”） */
  expectsReports: boolean;
  /** 本周“建议关注”里有这个人（只有直属主管能看到） */
  insight: { id: string; kind: InsightKind; headline: string; status: InsightStatus } | null;
  canViewReports: boolean;
  canEdit: boolean;
  isSelf: boolean;
}

/** 新建项目（POST /projects）。编号自动生成，负责人和参与人的日报会按这个项目预填 */
export interface NewProjectInput {
  name: string;
  ownerId: string;
  teamId: string | null;
  participantIds: string[];
  dueDate: string | null;
}

/** 批量导入项目（POST /admin/projects/import）的结果 */
export interface ImportProjectsResult {
  created: { code: string; name: string }[];
  skipped: { line: number; reason: string }[];
}

/** 修改项目基本信息（PUT /projects/:id/basics）：参与人按增减提交，避免冲掉别人同时做的加入 */
export interface EditProjectInput {
  name: string;
  ownerId: string;
  teamId: string | null;
  add: string[];
  remove: string[];
}

// ---------- 项目集与进度汇报 ----------
// 参考 PRINCE2 的项目启动文档（PID）和定期进度汇报（Highlight Report）：
// 项目经理把一组关键任务（Team 里的项目）放进一个项目集，记下计划基线；每期汇报对照基线写进展、延期原因、上期计划完成情况和下期计划。

/** 总体状态：正常 / 有风险 / 严重偏离 */
export type ProgramRag = 'green' | 'amber' | 'red';
export const RAG_LABEL: Record<ProgramRag, string> = { green: '正常', amber: '有风险', red: '严重偏离' };

/** 项目说明（PID 的精简版） */
export interface ProgramCharter {
  /** 背景与目标 */
  goal: string;
  /** 范围：做什么 */
  scope: string;
  /** 不做什么 */
  outOfScope: string;
  /** 交付物 */
  deliverables: string;
  /** 约束与容差：期限、预算、允许的偏差 */
  constraints: string;
}

export interface ProgramSummaryDTO {
  id: string;
  code: string;
  name: string;
  pm: PersonRef;
  sponsor: PersonRef | null;
  status: 'active' | 'closed';
  startDate: string | null;
  endDate: string | null;
  taskCount: number;
  doneCount: number;
  /** 计划基线之后才完成（或预计完成）的关键任务 */
  delayedCount: number;
  blockedCount: number;
  latestReport: { id: string; asOf: string; status: 'draft' | 'sent'; rag: ProgramRag } | null;
}

export interface ProgramTaskDTO {
  projectId: string;
  code: string;
  name: string;
  /** 分组（例如 25kW、25kW 降本、裂相机） */
  section: string | null;
  position: number;
  status: ProjectStatus;
  owner: PersonRef;
  /** 计划基线 */
  baselineStart: string | null;
  baselineEnd: string | null;
  /** 当前预计完成（项目截止日） */
  dueDate: string | null;
  doneAt: string | null;
  /** 相对基线的偏差天数：正数是延后，没有基线或截止日时为 null */
  slipDays: number | null;
  dueChanges: DueChangeDTO[];
  blockedReason: string | null;
  nextStep: { text: string; date: string | null; assigneeName: string | null } | null;
  latest: { text: string; date: string; memberName: string } | null;
  /** 看不到项目内容时，只显示名录信息 */
  visible: boolean;
}

export interface ProgramDTO extends ProgramSummaryDTO {
  teamId: string | null;
  charter: ProgramCharter;
  reportTo: PersonRef[];
  tasks: ProgramTaskDTO[];
  sections: string[];
  reports: { id: string; asOf: string; status: 'draft' | 'sent'; rag: ProgramRag }[];
  canEdit: boolean;
}

/** 关键任务这一期的进展 */
export interface ProgramReportRow {
  projectId: string;
  progress: string;
  delayNote: string;
  edited: boolean;
  sources: WeeklySource[];
}

export type PlanResult = 'done' | 'partial' | 'not_done';
export const PLAN_RESULT_LABEL: Record<PlanResult, string> = { done: '已完成', partial: '部分完成', not_done: '未完成' };

/** 计划事项：上期计划（带完成情况）和下期计划共用 */
export interface ProgramPlanItem {
  id: string;
  text: string;
  projectId: string | null;
  date: string | null;
  ownerId: string | null;
  result: PlanResult | null;
  note: string;
}

export interface ProgramReportDTO {
  id: string;
  programId: string;
  asOf: string;
  /** 这一期材料的起始日（上一期汇报的次日，第一期为 7 天前） */
  since: string;
  status: 'draft' | 'sent';
  rag: ProgramRag;
  /** 系统按任务状态和偏差算出的建议值 */
  ragSuggested: ProgramRag;
  summary: string;
  rows: ProgramReportRow[];
  /** 之前几期同一任务的进展（按日期从早到晚），表格里显示为历史行 */
  history: Record<string, { asOf: string; progress: string }[]>;
  lastPeriod: ProgramPlanItem[];
  nextPeriod: ProgramPlanItem[];
  risks: string;
  asks: string;
  generatedBy: 'ai' | 'rules';
  generatedAt: string;
  editedAt: string | null;
  sentAt: string | null;
  sentByName: string | null;
  canEdit: boolean;
}

export interface ProgramInput {
  name: string;
  pmId: string;
  sponsorId: string | null;
  teamId: string | null;
  startDate: string | null;
  endDate: string | null;
  reportToIds: string[];
}
